if (!customElements.get('vital-market-selector')) {
  class VitalMarketSelector extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.details = this.querySelector('details');
      this.search = this.querySelector('[data-vf-market-search]');
      this.countryInput = this.querySelector('input[name="country_code"]');
      this.localeInput = this.querySelector('input[name="locale_code"]');
      this.options = Array.from(this.querySelectorAll('[data-vf-market-option]'));
      this.items = Array.from(this.querySelectorAll('[data-vf-market-item]'));
      this.geoCountryOptions = Array.from(this.querySelectorAll('[data-vf-geo-country-option]'));

      this.storageGet = (key) => {
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          return null;
        }
      };

      this.storageSet = (key, value) => {
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          // Storage can be unavailable in private browsing or restricted contexts.
        }
      };

      this.submitLocalization = () => {
        if (!this.form) return;

        this.classList.add('is-loading');

        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.submit();
        }
      };

      this.onOptionClick = (event) => {
        const option = event.currentTarget;
        if (!this.form || !this.countryInput || !this.localeInput) return;

        this.storageSet('vf_market_manual_choice_v1', '1');
        this.storageSet('vf_market_geo_done_v1', '1');

        this.countryInput.value = option.dataset.countryCode || this.countryInput.value;
        this.localeInput.value = option.dataset.localeCode || this.localeInput.value;
        this.submitLocalization();
      };

      this.onSearch = () => {
        const term = (this.search?.value || '').trim().toLowerCase();
        this.items.forEach((item) => {
          const option = item.querySelector('[data-vf-market-option]');
          const haystack = (option?.dataset.search || option?.textContent || '').toLowerCase();
          item.hidden = term !== '' && !haystack.includes(term);
        });
      };

      this.onToggle = () => {
        if (this.details?.open && this.search) {
          window.setTimeout(() => this.search.focus({ preventScroll: true }), 50);
        }
      };

      this.onDocumentClick = (event) => {
        if (this.details?.open && !this.contains(event.target)) {
          this.details.open = false;
        }
      };

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.details?.open) {
          this.details.open = false;
          this.querySelector('summary')?.focus();
        }
      };

      this.detectVisitorCountry = async () => {
        if (!this.form || !this.countryInput || !this.localeInput) return;
        if (this.storageGet('vf_market_manual_choice_v1') === '1') return;
        if (this.storageGet('vf_market_geo_done_v1') === '1') return;

        // Respect deliberate language URLs and protect the unpublished theme preview.
        const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase() || '';
        const explicitLanguageUrl = ['da', 'de', 'es', 'fr', 'it', 'nl', 'no', 'pl', 'pt', 'sv'].includes(firstPathSegment);
        const activeLanguage = (this.localeInput.value || document.documentElement.lang || 'en').toLowerCase();
        const isThemePreview = new URLSearchParams(window.location.search).has('preview_theme_id')
          || window.Shopify?.designMode === true;
        if (explicitLanguageUrl || activeLanguage !== 'en' || isThemePreview) return;

        const userAgent = navigator.userAgent || '';
        if (/bot|crawler|spider|crawling|slurp|bingpreview|facebookexternalhit/i.test(userAgent)) {
          return;
        }

        const currentCountry = (window.Shopify?.country || this.countryInput.value || '').toUpperCase();
        const root = window.Shopify?.routes?.root || '/';

        try {
          const url =
            root +
            'browsing_context_suggestions.json' +
            '?country[enabled]=true' +
            (currentCountry ? '&country[exclude]=' + encodeURIComponent(currentCountry) : '');

          const response = await fetch(url, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });

          if (!response.ok) {
            return;
          }

          const data = await response.json();

          // Shopify returns a suggested country only when it offers a better
          // localization than the current storefront context.
          const countrySuggestion = data?.suggestions?.find((suggestion) => suggestion?.parts?.country)?.parts?.country;
          const suggestedCountry = (countrySuggestion?.handle || '').toUpperCase();
          const confidence = Number(countrySuggestion?.confidence ?? 0);

          if (!suggestedCountry || confidence < 0.9 || suggestedCountry === currentCountry) {
            this.storageSet('vf_market_geo_done_v1', '1');
            return;
          }

          const targetOption = this.geoCountryOptions.find(
            (option) => (option.dataset.countryCode || '').toUpperCase() === suggestedCountry
          );

          if (!targetOption) {
            this.storageSet('vf_market_geo_done_v1', '1');
            return;
          }

          // Re-check in case the customer manually selected a market while
          // the GeoIP request was still in flight.
          if (this.storageGet('vf_market_manual_choice_v1') === '1') return;

          this.storageSet('vf_market_geo_done_v1', '1');
          this.countryInput.value = targetOption.dataset.countryCode || this.countryInput.value;
          this.localeInput.value = targetOption.dataset.localeCode || this.localeInput.value;
          this.submitLocalization();
        } catch (error) {
          // Fail silently and leave the current storefront context unchanged.
          // Do not mark detection as complete so a later page can retry.
        }
      };

      this.options.forEach((option) => option.addEventListener('click', this.onOptionClick));
      this.search?.addEventListener('input', this.onSearch);
      this.details?.addEventListener('toggle', this.onToggle);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);
      window.setTimeout(() => this.detectVisitorCountry(), 0);

      
    }

    disconnectedCallback() {
      this.options?.forEach((option) => option.removeEventListener('click', this.onOptionClick));
      this.search?.removeEventListener('input', this.onSearch);
      this.details?.removeEventListener('toggle', this.onToggle);
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('keydown', this.onKeydown);
    }
  }

  customElements.define('vital-market-selector', VitalMarketSelector);
}